import { listenToEvent } from '../util/listen_to_event.js';
import { HTMLControl } from './html_control.js';

const defaultScale = 5;

/**
 * Rating control.
 */
class RatingControl extends HTMLControl {
  static tagName = 'rating-control';
  static get selectors() {
    const AllSegments = '.segment';
    return {
      AllSegments,
      Label: '.label',
      Rating: '.rating',
      ScaleLabel: '.scale-label',
      SegmentAt: index => `${AllSegments}:nth-child(${index})`,
      UnrateLink: '.unrate > .link',
    };
  }

  static elements = {
    allSegmentElements: this.selectors.AllSegments,
    labelElement: this.selectors.Label,
    ratingElement: this.selectors.Rating,
    scaleLabelElement: this.selectors.ScaleLabel,
    unrateLink: this.selectors.UnrateLink,
  };

  constructor() {
    super();
    this.createFragmentProperty('segment');
    listenToEvent(this, 'click', ev => {
      if (!this.readonly && this.isImg(ev.target)) {
        this.value = this.valueByImg(ev.target);
      }
    });
  }

  static properties = {
    label: {
      attribute: true,
      set(v) {
        this.labelElement.textContent = v;
      },
    },
    readonly: {
      attribute: 'boolean',
    },
  };

  initialize() {
    let scaleLabels = this.getAttribute('scale-labels');
    if (scaleLabels) {
      this._scaleLabels = scaleLabels.split(',').map(l => l.trim());
    }

    super.initialize();

    for (let i = 0; i < this.scale; i++) {
      this.ratingElement.insertBefore(
        this.segmentFragment,
        this.ratingElement.firstChild
      );
    }
    this.labelElement.textContent = this.label;
    this.drawValue(this.value);

    listenToEvent(this.unrateLink, 'click', () => this.unrate());
  }

  static get markup() {
    return `
<div class='header'>
  <div class='label'></div><div class='scale-label'></div>
</div>
<div class='rating'>
  <div class='unrate'>
    <span class='link'>Unrate</span>
  </div>
</div>
`;
  }

  static get segmentMarkup() {
    return `
      <div class='segment'>
        <div class='segment-image'></div>
        <div class='segment-image'></div>
      </div>
    `;
  }

  get scale() {
    return (this.scaleLabels && this.scaleLabels.length) || defaultScale;
  }

  get scaleLabels() {
    return this._scaleLabels;
  }
  set scaleLabels(v) {
    this._scaleLabels = v;
    if (v) {
      this.setAttribute('scale-labels', v.join(','));
    } else {
      this.removeAttribute('scale-labels');
    }
  }

  get value() {
    return parseFloat(this.getAttribute('value')) || 0;
  }
  set value(v) {
    this.drawValue(v);
    if (v > 0) {
      this.setAttribute('value', v);
    } else {
      this.removeAttribute('value');
    }
  }

  unrate() {
    this.value = 0;
  }

  drawValue(v) {
    if (v < 0 || v > this.scale) {
      throw new Error(`rating-control: wrong ${v} value`);
    }

    const segmentSize = parseInt(
      this.ownerDocument.defaultView
        .getComputedStyle(this)
        .getPropertyValue('--size')
    );

    let els = this.allSegmentElements;
    for (let i = 0; i < els.length; i++) {
      let imgStyle = this.getFillImg(els[i]).style;
      if (i < Math.floor(v)) {
        imgStyle.clip = 'unset';
      } else if (i == Math.floor(v) && i < Math.ceil(v)) {
        let ratio = segmentSize * (v - Math.floor(v));
        imgStyle.clip = `rect(0, ${Math.floor(ratio)}px, auto, 0)`;
      } else {
        imgStyle.clip = 'rect(0, 0, auto, 0)';
      }
    }

    if (this.scaleLabels) {
      this.scaleLabelElement.textContent = v > 0 ? this.scaleLabels[v - 1] : '';
    }
  }

  getFillImg(segmentEl) {
    return segmentEl.lastElementChild;
  }
  isImg(el) {
    return el.className == 'segment-image';
  }

  valueByImg(el) {
    let v = 0;
    for (
      let startEl = el.parentNode;
      startEl;
      startEl = startEl.previousElementSibling
    ) {
      v++;
    }
    return v;
  }
}

RatingControl.defineCustomElement();

export { RatingControl };
